import { useMemo, useState } from 'react';
import '../styles/maintenance-ui-mock.css';

type LayoutId = 'inspection' | 'vendor' | 'owner';

const layoutDirections = [
  {
    id: 'inspection' as const,
    title: '法定点検から始まる一元管理',
    shortTitle: '法定点検スタート',
    summary: '点検・写真・報告に絞って導入ハードルを下げる方向性。',
    highlights: ['法定点検が入口', '写真が自動で記録', '既存運用と共存'],
    switcherHint: '点検→写真→記録の導線',
  },
  {
    id: 'vendor' as const,
    title: '多業者を束ねるハブ',
    shortTitle: '多業者ハブ',
    summary: '部分管理の強みを活かし、業者連携と進捗共有を軸に。',
    highlights: ['業者ごとの進捗', '見積・発注の流れ', '連絡の一元化'],
    switcherHint: '業者連携と案件ボード',
  },
  {
    id: 'owner' as const,
    title: 'オーナー向けの超シンプルUI',
    shortTitle: 'オーナー簡潔',
    summary: '低リテラシーでも迷わない、最低限の情報と操作だけ。',
    highlights: ['今日やること', '写真・連絡中心', '用語を簡単に'],
    switcherHint: '大きいカードと短い言葉',
  },
];

const insightCards = [
  {
    title: '部分管理の入口を作る',
    body: '法定点検と写真管理を先に見せ、必要性を自然に理解してもらう。',
  },
  {
    title: '多業者管理で価値を示す',
    body: '複数の業者とのやり取りを一箇所で可視化し、負担軽減を体感させる。',
  },
  {
    title: 'アナログ運用と併走',
    body: '紙の資料と共存できるUIにし、安心感を残しながら移行できる。',
  },
];

function InspectionFirstLayout() {
  return (
    <div className="bm-layout bm-layout--inspection">
      <aside className="bm-inspect-sidebar">
        <div className="bm-inspect-brand">
          <span>一元帳</span>
          <span className="bm-inspect-brand-sub">法定点検入口</span>
        </div>
        <nav className="bm-inspect-nav">
          <button className="bm-nav-item is-active" type="button">ダッシュボード</button>
          <button className="bm-nav-item" type="button">法定点検</button>
          <button className="bm-nav-item" type="button">点検写真</button>
          <button className="bm-nav-item" type="button">報告書</button>
          <button className="bm-nav-item" type="button">連絡ノート</button>
          <button className="bm-nav-item" type="button">履歴</button>
        </nav>
        <div className="bm-inspect-side-note">
          紙の資料と併用できます
        </div>
      </aside>
      <div className="bm-inspect-content">
        <header className="bm-inspect-header">
          <div>
            <p className="bm-inspect-building">ニューザック第3ビル</p>
            <h2>法定点検から始まる一元管理</h2>
            <p className="bm-inspect-subtitle">点検写真が自動で記録されるシンプル導線</p>
          </div>
          <div className="bm-inspect-actions">
            <button type="button" className="bm-cta-primary">写真を撮る</button>
            <button type="button" className="bm-cta-secondary">点検を追加</button>
          </div>
        </header>
        <div className="bm-inspect-grid">
          <section className="bm-card bm-stagger" style={{ animationDelay: '60ms' }}>
            <h3>今月の法定点検</h3>
            <div className="bm-inspect-kpi-row">
              <div>
                <p className="bm-kpi-label">予定</p>
                <p className="bm-kpi-value">6件</p>
              </div>
              <div>
                <p className="bm-kpi-label">完了</p>
                <p className="bm-kpi-value">2件</p>
              </div>
              <div>
                <p className="bm-kpi-label">写真未登録</p>
                <p className="bm-kpi-value bm-kpi-alert">3件</p>
              </div>
            </div>
            <ul className="bm-simple-list">
              <li>消防設備点検 - 12/27 10:00</li>
              <li>エレベーター点検 - 12/29 14:00</li>
              <li>貯水槽清掃 - 01/05 09:00</li>
            </ul>
          </section>
          <section className="bm-card bm-stagger" style={{ animationDelay: '120ms' }}>
            <h3>作業者の導線</h3>
            <div className="bm-step-list">
              <div>
                <span className="bm-step-number">1</span>
                <p>予定を開く</p>
              </div>
              <div>
                <span className="bm-step-number">2</span>
                <p>写真を撮る</p>
              </div>
              <div>
                <span className="bm-step-number">3</span>
                <p>自動で一元帳に記録</p>
              </div>
            </div>
            <div className="bm-inspect-hint">
              「作業指示書」ではなく「点検メモ」に名称を変更
            </div>
          </section>
          <section className="bm-card bm-card-wide bm-stagger" style={{ animationDelay: '180ms' }}>
            <div className="bm-card-header">
              <h3>点検写真 → 自動記録</h3>
              <span className="bm-status-pill">自動保存</span>
            </div>
            <div className="bm-photo-stack">
              <div className="bm-photo-tile">写真</div>
              <div className="bm-photo-tile">写真</div>
              <div className="bm-photo-tile">写真</div>
            </div>
            <p className="bm-card-note">撮影した写真はそのまま点検記録に紐づく</p>
          </section>
          <section className="bm-card bm-stagger" style={{ animationDelay: '240ms' }}>
            <h3>次の提案ポイント</h3>
            <div className="bm-highlight-box">
              <p>点検だけの契約から、清掃・設備管理まで広げる</p>
            </div>
            <button type="button" className="bm-cta-link">フルパッケージ案を見る</button>
          </section>
        </div>
        <div className="bm-inspect-footer">
          <div>
            <p className="bm-footer-title">今週のやること</p>
            <p className="bm-footer-body">点検予定をまとめて共有 → 写真登録を促す</p>
          </div>
          <button type="button" className="bm-cta-primary">共有リンクを送る</button>
        </div>
      </div>
    </div>
  );
}

function VendorHubLayout() {
  return (
    <div className="bm-layout bm-layout--vendor">
      <aside className="bm-vendor-rail">
        <div className="bm-vendor-logo">MNT</div>
        <div className="bm-vendor-rail-items">
          <span className="bm-rail-dot is-active" />
          <span className="bm-rail-dot" />
          <span className="bm-rail-dot" />
          <span className="bm-rail-dot" />
        </div>
        <p className="bm-vendor-rail-label">多業者</p>
      </aside>
      <div className="bm-vendor-body">
        <header className="bm-vendor-header">
          <div>
            <p className="bm-vendor-kicker">部分管理でも成果を出す</p>
            <h2>多業者ハブ</h2>
          </div>
          <div className="bm-vendor-metrics">
            <div className="bm-metric-card">
              <p>連絡待ち</p>
              <strong>7件</strong>
            </div>
            <div className="bm-metric-card">
              <p>見積確認</p>
              <strong>3件</strong>
            </div>
            <div className="bm-metric-card alert">
              <p>緊急</p>
              <strong>2件</strong>
            </div>
          </div>
          <button type="button" className="bm-cta-primary">業者に一斉連絡</button>
        </header>
        <div className="bm-vendor-board">
          <section className="bm-vendor-column">
            <h3>依頼中</h3>
            <div className="bm-vendor-card">空調メンテ依頼 / 三星サービス</div>
            <div className="bm-vendor-card">排水ポンプ確認 / 東都設備</div>
          </section>
          <section className="bm-vendor-column">
            <h3>見積中</h3>
            <div className="bm-vendor-card">自動扉点検 / 北海メンテ</div>
          </section>
          <section className="bm-vendor-column">
            <h3>作業中</h3>
            <div className="bm-vendor-card">受水槽清掃 / アクア社</div>
            <div className="bm-vendor-card">照明交換 / 光電工業</div>
          </section>
          <section className="bm-vendor-column">
            <h3>完了</h3>
            <div className="bm-vendor-card done">消防設備点検 / アイ防災</div>
          </section>
        </div>
        <aside className="bm-vendor-sidepanel">
          <div className="bm-sidepanel-card">
            <h4>業者スナップショット</h4>
            <p>稼働中: 12社</p>
            <p>連絡中: 4社</p>
            <p>評価高: 6社</p>
          </div>
          <div className="bm-sidepanel-card">
            <h4>法定点検の入口</h4>
            <p>点検予定 6件 → 写真共有で即報告</p>
            <button type="button" className="bm-cta-secondary">点検フローを見る</button>
          </div>
          <div className="bm-sidepanel-card">
            <h4>オーナー向け説明</h4>
            <p>「連絡が増えても整理できます」</p>
            <button type="button" className="bm-cta-link">説明テンプレ</button>
          </div>
        </aside>
      </div>
    </div>
  );
}

function OwnerSimpleLayout() {
  return (
    <div className="bm-layout bm-layout--owner">
      <header className="bm-owner-header">
        <div>
          <p className="bm-owner-kicker">オーナー向け</p>
          <h2>今日のまとめ</h2>
        </div>
        <div className="bm-owner-status">
          連絡 1件 / 写真 5枚 / 点検 2件
        </div>
      </header>
      <section className="bm-owner-hero">
        <div className="bm-owner-card bm-owner-card--large">
          <h3>今日やること</h3>
          <ul>
            <li>点検写真を3枚登録</li>
            <li>設備Aの報告を確認</li>
            <li>清掃会社へ返信</li>
          </ul>
        </div>
        <div className="bm-owner-card bm-owner-card--large">
          <h3>点検写真</h3>
          <div className="bm-owner-photo-grid">
            <span>写真</span>
            <span>写真</span>
            <span>写真</span>
            <span>写真</span>
          </div>
          <p className="bm-owner-note">撮ったら自動で記録されます</p>
        </div>
      </section>
      <section className="bm-owner-grid">
        <div className="bm-owner-card">
          <h4>今週の予定</h4>
          <p>法定点検 2件 / 清掃 1件</p>
        </div>
        <div className="bm-owner-card">
          <h4>連絡ノート</h4>
          <p>「返信が必要」だけ表示</p>
        </div>
        <div className="bm-owner-card">
          <h4>困ったとき</h4>
          <p>電話で相談するボタンを用意</p>
        </div>
      </section>
      <footer className="bm-owner-footer">
        <button type="button">ホーム</button>
        <button type="button">点検</button>
        <button type="button" className="is-primary">写真</button>
        <button type="button">連絡</button>
        <button type="button">設定</button>
      </footer>
    </div>
  );
}

export function MaintenanceLayoutMockPage() {
  const [activeLayout, setActiveLayout] = useState<LayoutId>('inspection');
  const activeMeta = useMemo(
    () => layoutDirections.find((direction) => direction.id === activeLayout),
    [activeLayout]
  );

  return (
    <div className="bm-mock-page">
      <section className="bm-mock-hero">
        <div className="bm-mock-hero-copy">
          <span className="bm-mock-kicker">ビルメンテ専用UIモック</span>
          <h1>部分管理から「一元管理」へ自然につなげるためのダッシュボード案</h1>
          <p>
            法定点検を入口にしつつ、多業者管理の価値を見せる。
            リテラシーが低いオーナーでも迷わない構成を狙います。
          </p>
          <div className="bm-mock-pill-row">
            <span>法定点検起点</span>
            <span>写真自動登録</span>
            <span>多業者連携</span>
            <span>アナログ共存</span>
          </div>
        </div>
        <div className="bm-mock-hero-insights">
          {insightCards.map((card) => (
            <div key={card.title} className="bm-insight-card">
              <h3>{card.title}</h3>
              <p>{card.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bm-mock-directions">
        {layoutDirections.map((direction) => (
          <button
            key={direction.id}
            type="button"
            className={`bm-direction-card ${activeLayout === direction.id ? 'is-active' : ''}`}
            onClick={() => setActiveLayout(direction.id)}
          >
            <div className="bm-direction-card-header">
              <h3>{direction.shortTitle}</h3>
              <p>{direction.summary}</p>
            </div>
            <ul>
              {direction.highlights.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </button>
        ))}
      </section>

      <section className="bm-mock-preview">
        <div className="bm-mock-preview-header">
          <div>
            <span>現在の方向性</span>
            <h2>{activeMeta?.title}</h2>
          </div>
          <div className="bm-mock-preview-note">
            実際の動作は無視した静的モックです
          </div>
        </div>
        <div className="bm-mock-frame">
          <div key={activeLayout} className="bm-mock-layout">
            {activeLayout === 'inspection' && <InspectionFirstLayout />}
            {activeLayout === 'vendor' && <VendorHubLayout />}
            {activeLayout === 'owner' && <OwnerSimpleLayout />}
          </div>
        </div>
      </section>

    </div>
  );
}
